import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../api';

export default function MyDocuments() {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    Promise.all([
      api('/hcm/my-documents').then(r => r.ok ? r.json() : []),
      api('/hcm/my-document-folders').then(r => r.ok ? r.json() : []),
    ]).then(([docs, flds]) => { setDocuments(docs); setFolders(flds); }).finally(() => setLoading(false));
  }, []);

  const handleDownload = async (docId) => {
    const res = await api(`/hcm/my-documents/${docId}/url`);
    if (res.ok) {
      const data = await res.json();
      window.open(data.url, '_blank');
    }
  };

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    const res = await api(`/hcm/my-documents/${doc.id}/preview`);
    if (res.ok) {
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const isPreviewable = (fileName) => {
    const ext = (fileName || '').split('.').pop().toLowerCase();
    return ['pdf','docx','xlsx','pptx','doc','xls','ppt','odt','ods','odp','png','jpg','jpeg','gif','svg','webp'].includes(ext);
  };

  const isImage = (fileName) => {
    const ext = (fileName || '').split('.').pop().toLowerCase();
    return ['png','jpg','jpeg','gif','svg','webp'].includes(ext);
  };

  const visibleDocs = currentFolder
    ? documents.filter(d => d.folderId === currentFolder)
    : documents.filter(d => !d.folderId || !folders.some(f => f.id === d.folderId));

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">My Documents</h2>
      {currentFolder && (
        <nav className="flex items-center gap-1 mb-4 text-sm">
          <button onClick={() => setCurrentFolder(null)} className="text-indigo-600 hover:text-indigo-800">All Documents</button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-700 font-medium">{folders.find(f => f.id === currentFolder)?.name}</span>
        </nav>
      )}
      {!currentFolder && <div className="mb-6" />}

      {loading ? <p className="text-gray-500">Loading...</p> : (folders.length === 0 && documents.length === 0) ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No documents available.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {!currentFolder && folders.map(folder => (
            <div key={folder.id} className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentFolder(folder.id)}>
              <div className="flex items-center gap-3 min-w-0">
                <svg className="w-5 h-5 text-indigo-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                </svg>
                <p className="text-sm font-medium text-indigo-600">{folder.name}</p>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </div>
          ))}
          {visibleDocs.map(doc => (
            <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
              <div className="flex items-center gap-3 min-w-0">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                  <p className="text-xs text-gray-500">{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isPreviewable(doc.fileName) && (
                  <button onClick={() => handlePreview(doc)}
                    className="text-sm text-gray-500 hover:text-indigo-600 font-medium">
                    Preview
                  </button>
                )}
                <button onClick={() => handleDownload(doc.id)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                  Download
                </button>
              </div>
            </div>
          ))}
          {visibleDocs.length === 0 && !(!currentFolder && folders.length > 0) && (
            <div className="text-center py-8 text-gray-500 text-sm">No documents in this folder.</div>
          )}
        </div>
      )}

      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900 truncate">{previewDoc.fileName}</h3>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="flex-1 overflow-hidden">
              {!previewUrl ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : isImage(previewDoc.fileName) ? (
                <div className="flex items-center justify-center h-full p-4 overflow-auto">
                  <img src={previewUrl} alt={previewDoc.fileName} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <iframe src={previewUrl} title={previewDoc.fileName} className="w-full h-full border-0" />
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
